/**
 * <SupportButton> / `useSupport` — the entry to the Support module, for EVERY
 * user. `useSupport` owns everything (badge, panel, report dialog); a host
 * either renders the pill `SupportButton`, or — the running application's top
 * bar — puts `openPanel` / `openReport` in the user menu and renders `ui`.
 *
 * One button, one panel: report an issue, follow the ones you reported, and
 * answer the questions asked about them. The badge counts what is waiting on
 * the person looking at it — their issues the pipeline needs an answer on,
 * plus (for the support team) issues sitting at a gate their role decides —
 * never the total, which could not reach zero.
 *
 * The panel never navigates: the support team's board is the host's to open
 * (`onOpenBoard`), exactly as the work panel hands tasks back to its host.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { LayoutGrid, LifeBuoy, Plus } from 'lucide-react'

import type { SupportCounts, SupportMeta, SupportNotify, SupportRuntime } from './contract'
import IssueDetail from './IssueDetail'
import MyIssues from './MyIssues'
import ReportIssueDialog from './ReportIssueDialog'
import { ErrorLine, SupportDrawer, btn, errorText } from './ui'

export interface SupportButtonProps {
  runtime: SupportRuntime
  /** The meta the host already holds; fetched here when absent. */
  meta?: SupportMeta | null
  /** Host context added to every report (open screen, object, record, app version). */
  getContext?: () => Record<string, unknown>
  notify?: SupportNotify
  /** Opens the host's support board (team members only); absent → no link. */
  onOpenBoard?: () => void
  /** QC's "Open staging" inside an issue opened from this panel. */
  onOpenStaging?: (issueNumber: number) => void
  /** Badge refresh cadence in ms; 0 disables. */
  pollMs?: number
  label?: string
  /** Open the panel on this issue (e.g. from a notification). */
  openIssue?: number | null
  /** Open the panel on an issue NOW — a new `nonce` re-opens even the same
   *  issue (a work-list task clicked twice). */
  openRequest?: { issue: number; nonce: number } | null
  /** Opens the host's updates screen (a deployment's administrator, when
   *  `meta.pending_update` says a newer release is out); absent → no link. */
  onOpenUpdates?: () => void
}

function badgeOf(c?: SupportCounts | null): number {
  if (!c) return 0
  return (c.needs_info_mine || 0) + (c.waiting_on_me || 0)
}

export interface SupportControls {
  /** What is waiting on this user (their issues needing an answer, the
   *  team's gates, an administrator's pending update). */
  badge: number
  /** The hover line a host shows beside the entry. */
  title: string
  openPanel: () => void
  openReport: () => void
  /** The panel + report dialog — render once, anywhere; invisible until opened. */
  ui: ReactNode
}

const INERT: SupportControls = { badge: 0, title: '', openPanel: () => {}, openReport: () => {}, ui: null }

/** Support without a trigger of its own. `props` null → inert (no fetches,
 *  nothing rendered) — so a host can call it unconditionally. */
export function useSupport(props: SupportButtonProps | null): SupportControls {
  const {
    runtime, meta: metaProp, getContext, notify, onOpenBoard, onOpenStaging,
    pollMs = 60000, openIssue, openRequest, onOpenUpdates,
  } = props || ({} as Partial<SupportButtonProps>)
  const enabled = !!props && !!runtime
  const [meta, setMeta] = useState<SupportMeta | null>(metaProp || null)
  const [counts, setCounts] = useState<SupportCounts | null>(metaProp?.counts || null)
  const [open, setOpen] = useState(false)
  const [reporting, setReporting] = useState(false)
  const [current, setCurrent] = useState<number | null>(null)
  const [listKey, setListKey] = useState(0)
  const [metaError, setMetaError] = useState('')
  const alive = useRef(true)
  useEffect(() => () => { alive.current = false }, [])

  useEffect(() => {
    if (metaProp) { setMeta(metaProp); setCounts(metaProp.counts) }
  }, [metaProp])

  // Without a host-held meta, fetch it once — the panel needs the lifecycle
  // labels and the badge needs the counts.
  useEffect(() => {
    if (!enabled || !runtime || metaProp) return undefined
    let cancelled = false
    runtime.meta()
      .then((m) => { if (!cancelled) { setMeta(m); setCounts(m.counts); setMetaError('') } })
      .catch((e) => { if (!cancelled) setMetaError(errorText(e, 'Support is not available right now.')) })
    return () => { cancelled = true }
  }, [metaProp, runtime])

  const refreshCounts = useCallback(async () => {
    if (!runtime) return
    try {
      const c = runtime.counts ? await runtime.counts() : (await runtime.meta()).counts
      if (alive.current) setCounts(c)
    } catch {
      // A badge that cannot be read keeps its last value — showing zero would
      // claim nothing is waiting when we do not know.
    }
  }, [runtime])

  useEffect(() => {
    if (!enabled || !pollMs) return undefined
    const id = window.setInterval(() => { if (!open) void refreshCounts() }, pollMs)
    return () => window.clearInterval(id)
  }, [pollMs, open, refreshCounts])

  useEffect(() => {
    if (openIssue) { setCurrent(openIssue); setOpen(true) }
  }, [openIssue])
  useEffect(() => {
    if (openRequest?.issue) { setCurrent(openRequest.issue); setOpen(true) }
  }, [openRequest])

  const close = () => { setOpen(false); setCurrent(null); void refreshCounts() }
  // An administrator's "a newer version is out" counts as waiting on them.
  const pending = meta?.me.is_org_admin ? meta?.pending_update || null : null
  const badge = badgeOf(counts) + (pending ? 1 : 0)
  const team = !!meta?.me.is_team

  if (!enabled || !runtime) return INERT

  const ui = (
    <>
      <SupportDrawer
        open={open}
        onClose={close}
        title={current ? `Issue #${current}` : 'Support'}
        headerExtra={!current ? (
          <>
            {team && onOpenBoard && (
              <button type="button" style={btn('ghost', true)}
                onClick={() => { close(); onOpenBoard() }} title="Open the support team's board">
                <LayoutGrid size={12} /> Board
                {counts?.waiting_on_me ? ` (${counts.waiting_on_me})` : ''}
              </button>
            )}
            <button type="button" style={btn('primary', true)} onClick={() => setReporting(true)}>
              <Plus size={12} /> Report an issue
            </button>
          </>
        ) : undefined}
      >
        <ErrorLine message={metaError} />
        {pending && !current && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8625rem', padding: '0.5rem 0.65rem',
            borderRadius: 8, marginBottom: 8, border: '1px solid var(--sp-accent)',
          }}>
            <span style={{ flex: 1 }}>
              <strong>v{pending.version}</strong>{pending.title ? ` — ${pending.title}` : ''} is available
              {pending.count && pending.count > 1 ? ` (${pending.count} newer releases)` : ''}.
              {pending.pushed === false ? ' Its tag is not pushed yet.' : ''}
            </span>
            {onOpenUpdates && (
              <button type="button" style={btn('primary', true)} onClick={() => { close(); onOpenUpdates() }}>
                Review update
              </button>
            )}
          </div>
        )}
        {meta?.offline && !current && (
          <div style={{
            fontSize: '0.8425rem', padding: '0.45rem 0.6rem', borderRadius: 8, marginBottom: 8,
            border: '1px solid var(--sp-warning)', color: 'var(--sp-text)',
          }}>
            The support service cannot be reached right now. New reports are saved here and sent
            automatically{meta.queued ? ` (${meta.queued} waiting)` : ''}.
          </div>
        )}
        {current ? (
          <IssueDetail
            runtime={runtime}
            number={current}
            meta={meta}
            onBack={() => { setCurrent(null); setListKey((k) => k + 1) }}
            onOpenStaging={onOpenStaging}
            notify={notify}
          />
        ) : (
          <MyIssues runtime={runtime} meta={meta} onOpen={setCurrent} refreshKey={listKey} />
        )}
      </SupportDrawer>

      <ReportIssueDialog
        open={reporting}
        onClose={() => setReporting(false)}
        runtime={runtime}
        meta={meta}
        getContext={getContext}
        notify={notify}
        onCreated={(issue) => {
          setListKey((k) => k + 1)
          setOpen(true)
          setCurrent(issue.number)
          void refreshCounts()
        }}
        onQueued={() => {
          // Show it where the reporter will look for it: their list, at the top.
          setListKey((k) => k + 1)
          setOpen(true)
          setCurrent(null)
                }}
      />
    </>
  )

  return {
    badge,
    title: badge
      ? `Support — ${badge} waiting on you`
      : 'Support — report a problem or request a feature, and follow your issues',
    openPanel: () => setOpen(true),
    openReport: () => setReporting(true),
    ui,
  }
}

/** The pill: `useSupport` behind a top-bar button. */
export default function SupportButton(props: SupportButtonProps) {
  const s = useSupport(props)
  const label = props.label || 'Support'
  return (
    <>
      <button
        type="button"
        onClick={s.openPanel}
        title={s.title}
        style={{
          position: 'relative',
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.4rem 0.8rem',
          borderRadius: 999, fontWeight: 600, fontSize: '0.8825rem',
          background: 'var(--color-surface-2, var(--color-bg-secondary, #f1f5f9))',
          color: 'var(--color-text, #0f172a)',
          border: '1px solid var(--color-border, #e2e8f0)', cursor: 'pointer',
        }}
      >
        <LifeBuoy size={15} />
        {label}
        {s.badge > 0 && (
          <span style={{
            minWidth: 18, height: 18, padding: '0 5px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1,
            borderRadius: 9, background: 'var(--color-accent, #4c8dff)', color: '#fff',
          }}>
            {s.badge > 99 ? '99+' : s.badge}
          </span>
        )}
      </button>
      {s.ui}
    </>
  )
}
